<script setup lang="ts">
import type { PropertyId } from "~/classes/entity"

import { mediaType, mimeTypeString, PropertySchema } from "~/classes/entity"
import { FileUserRole } from "~/classes/file/properties/FileUserRole"

const props = defineProps({
  propertyId: null,
  multiline: { type: Boolean },
  label: null,
  values: null
})

/** Returns the same shared value */
const sameValue = computed(() => {
  if (identical(Object.values(props.values)))
    return first(props.values)
  else return undefined
})
</script>

<template>
  <panel-details-property
    :property-id
    :multiline
    :label
  >
    <template v-if="sameValue">
      <template v-if="sameValue.type === PropertySchema.SizeBytesValue">
        {{ formatBytes(sameValue.sizeBytes, 2) }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.UtcTimestampMillisValue">
        {{ (new Date(sameValue.utcTimestampMillis)).toLocaleString() }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.MimeTypeValue">
        <span class="capitalize">{{ mediaType(sameValue.mimeType) }} </span>
        <span> ({{ mimeTypeString(sameValue.mimeType) }})</span>
      </template>
      <template v-else-if="sameValue.type === PropertySchema.NonEmptyStringValue">
        {{ sameValue.nonEmptyString }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.AnyStringValue">
        {{ sameValue.anyString }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.AnyIntegerValue">
        {{ sameValue.anyInteger }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.PositiveFloatValue">
        {{ sameValue.positiveFloat }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.NonNegativeIntegerValue">
        {{ sameValue.nonNegativeInteger }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.RoleUuidValue">
        {{ (new FileUserRole(sameValue.role)).displayText() }}
      </template>
      <template v-else-if="sameValue.type === PropertySchema.WebUrlValue">
        <a
          :href="sameValue.url"
          target="_blank"
          class="external-url"
        >
          <icon-property
            type="external"
            size="xs"
            class="icon"
          />
          <div class="text">{{ sameValue.url }}</div>
        </a>
      </template>
      <template v-else>
        An error occurred...
      </template>
    </template>
    <panel-details-property-placeholder v-else />
  </panel-details-property>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.external-url
  @apply  flex gap-0.5
  .icon
    @apply text-$global-steel-primary flex-none
  .text
    @apply text-$global-blue-primary break-all
</style>
